import { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { AlertExpressionDropdownProps, DropdownOption, FixedExpressionOutputTypes } from './alertExpressionDropdown.typings'
import { ChartIndicator } from '../alertModal.typings'
import { AlertExpression, AlertExpression_Type, Condition_SecurityAlertCondition_Operator } from '../../../proto/generated/nexus'
import { Box, MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectAlertModalData } from '../../../selectors/alerts'
import { selectIndicators } from '../../../selectors/chart'
import {
    formatSecurityLabelForAlert,
    getAlertCompareSecurityTargets,
    getOperatorSource,
    getSelectedValue,
    isIndicatorOutputSelectableForAlertUi,
    getDrawingAlertDisplayLabel,
    buildFibonacciLevelMenuItems,
    isFibonacciChannelAlert,
} from '../../../util/alerts'
import { FIBONACCI_ALERT_LEVEL_OPTION_KEY } from '../../../util/fibonacci'
import { useStyles } from './styles'
import { VOLUME_INDICATOR_CODE } from '../../../assets/constants'
import { convertMapToKeyValueArray } from '../../../util/common'
import { TimeBucket_TimeSeries } from '../../../proto/generated/common'
import { getSecurityKey } from '../../../util/security'
import { getIndicatorDisplayNameForAlert } from '../../../util/indicators'
import { alertModalMenuProps } from '../alertModal'

/** Menu value prefix so compare-symbol options do not collide with indicator names. */
const COMPARE_MENU_PREFIX = '__cmp__:'

const COMPARE_SOURCE_OPTIONS: { key: string; label: string }[] = [
    { key: 'open', label: 'Open' },
    { key: 'high', label: 'High' },
    { key: 'low', label: 'Low' },
    { key: 'close', label: 'Close' }
]

const AlertExpressionDropdown: React.FC<AlertExpressionDropdownProps> = ({
                                                                             isConditionalOrder,
                                                                             expression,
                                                                             chartSnapshot,
                                                                             mode,
                                                                             onExpressionChange,
                                                                             className,
                                                                             comparedExpression,
                                                                             onOutputChange,
                                                                             fixedValueScale = 1,
                                                                             operator
                                                                         }) => {
    const {classes} = useStyles()
    const {expression: spreadExpression, expressionMode, securityList} = chartSnapshot
    const scale = fixedValueScale > 0 ? fixedValueScale : 1
    const [price, setPrice] = useState<string>(() => {
        const raw = expression.fixedValue
        if (raw == null || Number.isNaN(raw)) return '0'
        return scale !== 1 ? String(raw / scale) : String(raw)
    })
    const indicators = useSelector(selectIndicators)
    const alertModalData = useSelector(selectAlertModalData)

    /** Basket / multi-leg conditional: full catalog; else chart `indicators` only (from modal payload + snapshot). */
    const useFullIndicatorCatalogForConditionalMenu = useMemo(() => {
        if (!isConditionalOrder) return false
        return (
            alertModalData?.conditionalUseFullIndicatorCatalog ??
            (Boolean(alertModalData?.conditionalModalChartVariants) || chartSnapshot.expressionMode)
        )
    }, [
        isConditionalOrder,
        alertModalData?.conditionalUseFullIndicatorCatalog,
        alertModalData?.conditionalModalChartVariants,
        chartSnapshot.expressionMode,
    ])

    const fallbackSnapshotIndicators = useMemo(() => {
        return indicators
            .filter((i) => i.enabled && i.isAlertSupported && i.code !== VOLUME_INDICATOR_CODE)
            .map((i) => {
                const defaultConfig = (i.inputs || []).reduce(
                    (acc, input) => ({
                        ...acc,
                        [input.code]: input.defval
                    }),
                    {} as Record<string, string>
                )
                return {
                    id: i.code,
                    name: i.name,
                    displayName: getIndicatorDisplayNameForAlert(i, defaultConfig, indicators),
                    code: i.code,
                    shortName: i.shortName,
                    outputValues: {},
                    inputs: i.inputs,
                    outputs: i.outputs,
                    config: defaultConfig
                }
            })
    }, [indicators])
    const snapshotIndicators = isConditionalOrder
        ? fallbackSnapshotIndicators
        : chartSnapshot.indicators

    /** Technical-indicator rows shown in the LHS/RHS dropdown (chart-only vs full catalog for basket conditionals). */
    const menuSourceIndicators = useMemo(
        () =>
            isConditionalOrder && useFullIndicatorCatalogForConditionalMenu
                ? snapshotIndicators
                : chartSnapshot.indicators,
        [isConditionalOrder, useFullIndicatorCatalogForConditionalMenu, chartSnapshot.indicators, snapshotIndicators]
    )

    const indicatorMap: Map<string, ChartIndicator> = useMemo(() => {
        const map = new Map<string, ChartIndicator>()
        menuSourceIndicators.forEach((i) => map.set(i.id, i))
        return map
    }, [menuSourceIndicators])

    const comparedMode = mode === 'lhs' ? ('rhs' as const) : ('lhs' as const)

    useEffect(() => {
        setPrice(expression.fixedValue?.toString() || '0')
    }, [expression?.fixedValue])

    const expressionShowsCompare = useMemo(
        () =>
            expression.type === AlertExpression_Type.SIMPLE_SECURITY &&
            !!expression.simpleSecurity?.security &&
            expression.simpleSecurity.source !== FixedExpressionOutputTypes.VOLUME.toString() &&
            getSelectedValue(expression, indicators, { chartSnapshot, mode })[0] === FixedExpressionOutputTypes.COMPARE_SECURITY,
        [expression, indicators, chartSnapshot, mode]
    )

    const showCompareSecurityOption = useMemo(() => {
        if (mode === 'lhs' && chartSnapshot.expressionMode) return false
        return (chartSnapshot.comparableSecurities?.length ?? 0) > 0 || expressionShowsCompare
    }, [mode, chartSnapshot.comparableSecurities, chartSnapshot.expressionMode, expressionShowsCompare])

    const compareTargets = useMemo(() => {
        const base = getAlertCompareSecurityTargets(chartSnapshot)
        if (expression.type === AlertExpression_Type.SIMPLE_SECURITY && expression.simpleSecurity?.security) {
            const s = expression.simpleSecurity.security
            const k = getSecurityKey(s.exchange, s.code)
            if (!base.some((x) => getSecurityKey(x.exchange, x.code) === k)) {
                return [...base, s]
            }
        }
        return base
    }, [chartSnapshot, expression])

    const primaryChartSecurityKey = useMemo(
        () => getSecurityKey(securityList[0].exchange, securityList[0].code),
        [securityList]
    )

    const [compareSecurityMenuLabels, setCompareSecurityMenuLabels] = useState<Record<string, string>>({})

    useEffect(() => {
        let cancelled = false
        if (!showCompareSecurityOption || compareTargets.length === 0) {
            setCompareSecurityMenuLabels({})
            return
        }
        void (async () => {
            const next: Record<string, string> = {}
            for (const s of compareTargets) {
                const k = getSecurityKey(s.exchange, s.code)
                if (mode === 'lhs' && k === primaryChartSecurityKey) continue
                next[k] = await formatSecurityLabelForAlert(s)
            }
            if (!cancelled) setCompareSecurityMenuLabels(next)
        })()
        return () => {
            cancelled = true
        }
    }, [compareTargets, showCompareSecurityOption, mode, primaryChartSecurityKey])

    const sourceDrawing = alertModalData?.sourceDrawing ?? expression.drawing
    const drawingMenuLabel = useMemo(
        () => getDrawingAlertDisplayLabel(sourceDrawing?.drawingType),
        [sourceDrawing?.drawingType]
    )

    const menuItems: DropdownOption[] = useMemo(() => {
        const options: DropdownOption[] = []
        if (mode === 'lhs') {
            options.push({
                label: 'Price',
                value: FixedExpressionOutputTypes.PRICE
            })
            if (showCompareSecurityOption) {
                for (const s of compareTargets) {
                    const k = getSecurityKey(s.exchange, s.code)
                    if (k === primaryChartSecurityKey) continue
                    options.push({
                        label: compareSecurityMenuLabels[k] ?? s.code,
                        value: `${COMPARE_MENU_PREFIX}${k}`
                    })
                }
            }
        } else {
            if (sourceDrawing?.drawingType) {
                options.push({
                    label: drawingMenuLabel,
                    value: FixedExpressionOutputTypes.DRAWING
                })
            }
            options.push({
                label: 'Value',
                value: FixedExpressionOutputTypes.VALUE
            })
            if (showCompareSecurityOption) {
                for (const s of compareTargets) {
                    const k = getSecurityKey(s.exchange, s.code)
                    options.push({
                        label: compareSecurityMenuLabels[k] ?? s.code,
                        value: `${COMPARE_MENU_PREFIX}${k}`
                    })
                }
            }
        }
        if (comparedExpression) {
            const sec = comparedExpression.simpleSecurity || comparedExpression.spreadSecurity
            if (mode === 'lhs' || (!sec || sec.source !== FixedExpressionOutputTypes.VOLUME)) {
                options.push({
                    label: 'Volume',
                    value: FixedExpressionOutputTypes.VOLUME
                })
            }
        }

        menuSourceIndicators.forEach((ind) => {
            if (ind.code === VOLUME_INDICATOR_CODE) return
            const indicator = indicators.find((v) => v.code === ind.code)
            if (!indicator?.isAlertSupported) return

            if (mode === 'rhs' && comparedExpression && comparedExpression.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
                const [id, output] = getSelectedValue(comparedExpression, indicators, {
                    chartSnapshot,
                    mode: comparedMode
                })
                if (id === ind.id && ind.outputs.length === 1) return
            }
            options.push({ label: ind.displayName, value: ind.id })
        })
        return options
    }, [
        mode,
        comparedExpression,
        chartSnapshot,
        menuSourceIndicators,
        indicators,
        showCompareSecurityOption,
        comparedMode,
        compareTargets,
        primaryChartSecurityKey,
        compareSecurityMenuLabels,
        sourceDrawing?.drawingType,
        drawingMenuLabel,
    ])

    const selectedValue = useMemo(() => {
        return getSelectedValue(expression, indicators, { chartSnapshot, mode })
    }, [expression, indicators, chartSnapshot, mode])

    const showCompareRows = expressionShowsCompare

    const compareSecuritySelectValue = expression.simpleSecurity?.security
        ? getSecurityKey(expression.simpleSecurity.security.exchange, expression.simpleSecurity.security.code)
        : ''

    const primaryMenuSelectValue = useMemo(() => {
        if (expressionShowsCompare && compareSecuritySelectValue) {
            return `${COMPARE_MENU_PREFIX}${compareSecuritySelectValue}`
        }
        if (expression.type === AlertExpression_Type.TECHNICAL_INDICATOR && expression.indicator?.indicatorCode) {
            const code = expression.indicator.indicatorCode
            if (menuItems.some((m) => m.value === code)) return code
        }
        const v = selectedValue[0]
        if (menuItems.some((m) => m.value === v)) return v
        return v
    }, [expressionShowsCompare, compareSecuritySelectValue, selectedValue, expression.type, expression.indicator?.indicatorCode, menuItems])

    const defaultCompareSource = useMemo(
        () => getOperatorSource(operator ?? Condition_SecurityAlertCondition_Operator.CROSSING),
        [operator]
    )

    const onSelect = useCallback(
        (event: SelectChangeEvent<string>) => {
            const value = event.target.value
            if (value.startsWith(COMPARE_MENU_PREFIX)) {
                const key = value.slice(COMPARE_MENU_PREFIX.length)
                const sec = compareTargets.find((s) => getSecurityKey(s.exchange, s.code) === key)
                if (!sec) return
                const op = operator ?? Condition_SecurityAlertCondition_Operator.CROSSING
                const exp: AlertExpression = {
                    type: AlertExpression_Type.SIMPLE_SECURITY,
                    simpleSecurity: {
                        security: { exchange: sec.exchange, code: sec.code },
                        source: getOperatorSource(op)
                    },
                    timeBucket: undefined,
                    indicator: undefined,
                    fixedValue: undefined,
                    spreadSecurity: undefined
                }
                onExpressionChange(exp, 'type')
                return
            }
            let exp: AlertExpression = {
                type: expressionMode ? AlertExpression_Type.SPREAD_SECURITY : AlertExpression_Type.SIMPLE_SECURITY,
                timeBucket: {
                    timeSeries: TimeBucket_TimeSeries.SECOND,
                    value: 1
                },
                simpleSecurity: expressionMode
                    ? undefined
                    : {
                          security: { code: securityList[0].code, exchange: securityList[0].exchange },
                          source: 'close'
                      },
                spreadSecurity: expressionMode
                    ? {
                          security: securityList.map((v) => ({ code: v.code, exchange: v.exchange })),
                          source: 'close',
                          spreadExpression
                      }
                    : undefined
            }
            switch (value) {
                case FixedExpressionOutputTypes.PRICE:
                    break
                case FixedExpressionOutputTypes.VOLUME:
                    if (exp.simpleSecurity) exp.simpleSecurity.source = 'volume'
                    if (exp.spreadSecurity) exp.spreadSecurity.source = 'volume'
                    exp.timeBucket = undefined
                    break
                case FixedExpressionOutputTypes.VALUE:
                    exp.type = AlertExpression_Type.FIXED_VALUE
                    exp.fixedValue = 0
                    exp.timeBucket = undefined
                    exp.drawing = undefined
                    break
                case FixedExpressionOutputTypes.DRAWING: {
                    const drawing = sourceDrawing ?? expression.drawing
                    if (!drawing) break
                    onExpressionChange(
                        {
                            type: AlertExpression_Type.DRAWING,
                            drawing,
                            fixedValue: undefined,
                            indicator: undefined,
                            simpleSecurity: undefined,
                            spreadSecurity: undefined,
                            timeBucket: undefined,
                        },
                        'type'
                    )
                    return
                }
                default: {
                    const indicator = indicatorMap.get(value)
                    if (indicator) {
                        const visibleOutputs = indicator.outputs.filter(isIndicatorOutputSelectableForAlertUi)
                        const selectedOutputTitle = indicator.outputs.find((o) => o.code === selectedValue[1])?.title
                        const keepCurrent =
                            visibleOutputs.some((o) => o.code === selectedValue[1]) &&
                            Boolean(selectedOutputTitle && indicator.outputValues[selectedOutputTitle])
                        let newOutput = keepCurrent ? selectedValue[1] : visibleOutputs[0]?.code ?? indicator.outputs[0].code
                        if (mode === 'rhs' && comparedExpression && comparedExpression.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
                            const [id, output] = getSelectedValue(comparedExpression, indicators, {
                                chartSnapshot,
                                mode: comparedMode
                            })
                            const selectedIndicator = indicatorMap.get(value)

                            if (selectedIndicator && selectedIndicator.id === id && selectedIndicator.outputs.length > 1) {
                                const alt = selectedIndicator.outputs
                                    .filter((o) => o.code !== output && isIndicatorOutputSelectableForAlertUi(o))
                                    .map((o) => o.code)[0]
                                if (alt) newOutput = alt
                            }
                        }
                        exp = {
                            type: AlertExpression_Type.TECHNICAL_INDICATOR,
                            timeBucket: undefined,
                            indicator: {
                                indicatorCode: indicator.code,
                                params: convertMapToKeyValueArray(indicator.config),
                                output: newOutput,
                                expression: { ...exp, timeBucket: undefined }
                            }
                        }
                    }
                }
            }
            onExpressionChange(exp, 'type')
        },
        [
            expressionMode,
            securityList,
            spreadExpression,
            onExpressionChange,
            indicatorMap,
            selectedValue,
            mode,
            comparedExpression,
            indicators,
            chartSnapshot,
            compareTargets,
            operator,
            comparedMode,
            sourceDrawing,
            expression.drawing,
        ]
    )

    const onCompareSourceChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            if (!expression.simpleSecurity) return
            onExpressionChange(
                {
                    ...expression,
                    type: AlertExpression_Type.SIMPLE_SECURITY,
                    simpleSecurity: {
                        ...expression.simpleSecurity,
                        source: event.target.value
                    },
                    spreadSecurity: undefined,
                    indicator: undefined,
                    fixedValue: undefined
                },
                'simpleSecurity'
            )
        },
        [expression, onExpressionChange]
    )

    const selectedIndicator = useMemo(() => {
        const fromSelected = indicatorMap.get(selectedValue[0])
        if (fromSelected) return fromSelected
        if (expression.type === AlertExpression_Type.TECHNICAL_INDICATOR && expression.indicator?.indicatorCode) {
            return indicatorMap.get(expression.indicator.indicatorCode)
        }
        return undefined
    }, [indicatorMap, selectedValue, expression.type, expression.indicator?.indicatorCode])

    const isFibonacciDrawingRhs =
        mode === 'rhs' &&
        expression.type === AlertExpression_Type.DRAWING &&
        expression.drawing?.drawingType === 'fibonacci'
    // Channel ops use dedicated Upper/Lower bound rows — hide the single-level picker.
    const showFibonacciLevelPicker =
        isFibonacciDrawingRhs && !isFibonacciChannelAlert(expression.drawing?.drawingType, operator)

    const outputMenuItems: Array<{ key: string; label: string }> = useMemo(() => {
        if (showFibonacciLevelPicker) {
            return buildFibonacciLevelMenuItems(expression.drawing)
        }
        if (!selectedIndicator) return []
        if (mode === 'rhs' && comparedExpression && comparedExpression.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
            const [id, output] = getSelectedValue(comparedExpression, indicators, {
                chartSnapshot,
                mode: comparedMode
            })
            if (selectedIndicator.id === id && selectedIndicator.outputs.length > 1) {
                return selectedIndicator.outputs
                    .filter((o) => o.code !== output && isIndicatorOutputSelectableForAlertUi(o))
                    .map((o) => ({
                        key: o.code,
                        label: o.title
                    }))
            }
        }
        return selectedIndicator.outputs.filter(isIndicatorOutputSelectableForAlertUi).map((o) => ({ key: o.code, label: o.title }))
    }, [
        showFibonacciLevelPicker,
        expression.drawing,
        selectedIndicator,
        mode,
        comparedExpression,
        indicators,
        chartSnapshot,
        comparedMode,
    ])

    const showOutputDropdown = useMemo(
        () => (showFibonacciLevelPicker ? outputMenuItems.length > 0 : outputMenuItems.length > 1),
        [showFibonacciLevelPicker, outputMenuItems]
    )

    const outputSelectValue = useMemo(() => {
        const cur = selectedValue[1]
        if (outputMenuItems.some((o) => o.key === cur)) return cur
        return outputMenuItems[0]?.key ?? ''
    }, [outputMenuItems, selectedValue])

    useEffect(() => {
        if (outputMenuItems.length === 0) return
        const cur = selectedValue[1]
        if (outputMenuItems.some((o) => o.key === cur)) return
        const first = outputMenuItems[0].key
        if (first) onOutputChange(first)
    }, [outputMenuItems, selectedValue, onOutputChange])

    const onOutputSelectorChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            if (showFibonacciLevelPicker && expression.drawing) {
                onExpressionChange(
                    {
                        ...expression,
                        type: AlertExpression_Type.DRAWING,
                        drawing: {
                            ...expression.drawing,
                            options: {
                                ...(expression.drawing.options ?? {}),
                                [FIBONACCI_ALERT_LEVEL_OPTION_KEY]: event.target.value,
                            },
                        },
                    },
                    'drawing'
                )
                return
            }
            onOutputChange(event.target.value)
        },
        [showFibonacciLevelPicker, expression, onExpressionChange, onOutputChange]
    )

    const onFixedValueChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const inputValue = event.target.value
            const newValue = inputValue.replace(/^0+(?!$)/, '')
            setPrice(newValue)
            const n = Number(newValue)
            const native = scale !== 1 && Number.isFinite(n) ? n * scale : n
            onExpressionChange(
                {
                    ...expression,
                    fixedValue: Number.isFinite(native) ? native : 0,
                },
                'fixedValue'
            )
        },
        [expression, onExpressionChange, scale]
    )

    const showValueInput = useMemo(
        () => expression.type === AlertExpression_Type.FIXED_VALUE && expression.fixedValue !== undefined,
        [expression]
    )

    const handleBlur = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, isTriggerPrice: boolean = false) => {
        // const value = Number(e.target.value) > 0 ? Number(e.target.value) : 0
        // setPrice(value.toString())
    }, [])

    const compareSourceValue = expression.simpleSecurity?.source || defaultCompareSource

    return (
        <Box className={className}>
            <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Select<string>
                        className={
                            showOutputDropdown || showValueInput || showCompareRows ? classes.halfWidth : classes.fullWidth
                        }
                        size="small"
                        value={primaryMenuSelectValue}
                        onChange={onSelect}
                        MenuProps={alertModalMenuProps}
                    >
                        {menuItems.map((m, i) => (
                            <MenuItem key={`${m.value}-${i}`} value={m.value}>
                                {m.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {showOutputDropdown && (
                        <Select className={classes.halfWidth} size="small" value={outputSelectValue} onChange={onOutputSelectorChange} MenuProps={alertModalMenuProps}>
                            {outputMenuItems.map((o) => (
                                <MenuItem key={o.key} value={o.key}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    {showValueInput && (
                        <TextField
                            InputProps={{ sx: { '& input[type=number]': { MozAppearance: 'textfield' } } }}
                            className={classes.halfWidth}
                            size="small"
                            type="number"
                            onBlur={handleBlur}
                            value={price}
                            onChange={onFixedValueChange}
                        />
                    )}
                    {showCompareRows && (
                        <Select<string>
                            className={classes.halfWidth}
                            size="small"
                            value={compareSourceValue}
                            onChange={onCompareSourceChange}
                            MenuProps={alertModalMenuProps}
                        >
                            {COMPARE_SOURCE_OPTIONS.map((o) => (
                                <MenuItem key={o.key} value={o.key}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </Stack>
            </Stack>
        </Box>
    )
}

export default memo(AlertExpressionDropdown)
